// Reusable scored-call list (All / Above / Below tabs) with a
// click-to-inspect detail panel. Embedded in entity drawers and the
// page-level Browse Calls drawer. Backed by /scoring/scores/
// clickhouse/calls. The active tier is sent as the query's `threshold`
// so the server's row cap applies AFTER the tier filter (CUS-428: a
// big org fills `limit` rows in under a day, and filtering only that
// slice in the browser dropped every older 0-score call). Rows still
// carry their thresholds, so the same tier classification runs
// client-side on whatever comes back.

import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'
import classNames from 'classnames'

import { fetchingAPI, apiService } from '@/api'
import { Loader } from '@/components/loaders/Loader'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { SegmentedControl } from '@/components/buttons/SegmentedControl'
import { ExternalCallIdsLink } from '@/views/QACopilot/components/ExternalCallIdsLink'

import { buildDashboardParams } from '../helpers/dashboardParams'
import {
  TIER,
  callPercentage,
  tierForCall,
  isCallScored,
  filterCallsByTier,
  countCallsByTier,
  formatTimestamp,
  durationMinutes,
  TierBadge,
  downloadCsvFile,
} from '../helpers/format'
import { ExportCsvButton } from '../components/ExportCsvButton'
import { buildAgentNameMap, buildScorecardNameMap } from './entityDrawer.helpers'
import { ScoredCallDetailPanel } from './ScoredCallDetailPanel'

// Tier tab -> the BE `threshold` filter with the same definition
// (below = score < improvement_threshold, above = score >= exceeds_threshold).
const TIER_THRESHOLD = Object.freeze({
  [TIER.BELOW]: 'improvement',
  [TIER.ABOVE]: 'exceeds',
})

const initialTierFor = (initialTier) => {
  if (initialTier === 'above') {
    return TIER.ABOVE
  }
  if (initialTier === 'below') {
    return TIER.BELOW
  }
  return TIER.ALL
}

// The old Scores-page print report carried its context (date range) in
// a header block; the CSV carries it in the filename instead so the
// download is self-describing: scored-calls_2026-03-01_2026-03-31.csv
const buildScoredCallsCsvName = (scopeFilters) => {
  const day = (value) => {
    if (!value) {
      return null
    }
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10)
  }
  const from = day(scopeFilters?.startDate)
  const to = day(scopeFilters?.endDate)
  return from && to ? `scored-calls_${from}_${to}.csv` : 'scored-calls.csv'
}

export const ScoredCallsBrowser = ({ scopeFilters, orgId, limit = 1000, initialTier }) => {
  // fetchingAPI wants a dispatch for its token-expiry logout path.
  const dispatch = useDispatch()
  const dashboardAgents = useSelector((state) => state.qaCopilot.data?.agents)
  const dashboardScorecards = useSelector((state) => state.qaCopilot.data?.scorecards)
  const agentNames = useMemo(() => buildAgentNameMap(dashboardAgents), [dashboardAgents])
  const scorecardNames = useMemo(
    () => buildScorecardNameMap(dashboardScorecards),
    [dashboardScorecards]
  )

  // { url, data }: the data plus the request it answered, so a response
  // from a previous tier/scope is never mistaken for the current one.
  const [response, setResponse] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [activeTier, setActiveTier] = useState(() => initialTierFor(initialTier))
  // Per-tab counts we actually know. A tab's count is only real once a
  // fetch covering it came back un-truncated: the All fetch learns all
  // three, a tier fetch learns its own. Reset when the scope changes.
  const [knownCounts, setKnownCounts] = useState({})
  const [selectedCall, setSelectedCall] = useState(null)

  const scopeKey = JSON.stringify(scopeFilters || {})
  // Key the fetch on the SERIALIZED scope, not the scopeFilters object:
  // every caller builds scopeFilters inline, so its identity changes on
  // each parent render and object-keyed deps re-fired this 500-row
  // fetch 2-3x per drawer open (and again on any page re-render).
  const requestUrl = useMemo(() => {
    if (!orgId) {
      return null
    }
    const params = buildDashboardParams(scopeFilters || {}, orgId)
    params.set('limit', String(limit))
    const threshold = TIER_THRESHOLD[activeTier]
    if (threshold) {
      params.set('threshold', threshold)
    }
    return `${apiService.scorecard}/scoring/scores/clickhouse/calls?${params.toString()}`
  }, [orgId, scopeKey, limit, activeTier])

  // A scope change invalidates everything: drop the old rows (so they
  // never sit under the new filters while the fetch is pending) and the
  // counts. A tier change keeps both -- see the table condition below.
  useEffect(() => {
    setResponse(null)
    setKnownCounts({})
  }, [orgId, scopeKey, limit])

  useEffect(() => {
    if (!requestUrl) {
      setResponse(null)
      return undefined
    }
    let cancelled = false
    const tier = activeTier
    setLoading(true)
    setError(false)
    fetchingAPI(requestUrl, 'GET', dispatch)
      .then((data) => {
        if (cancelled) {
          return
        }
        setResponse({ url: requestUrl, data })
        if (!data?.truncated) {
          const counts = countCallsByTier(data?.items)
          setKnownCounts((prev) =>
            tier === TIER.ALL
              ? { all: counts.all, above: counts.above, below: counts.below }
              : { ...prev, [tier]: counts[tier] }
          )
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [requestUrl, dispatch])

  // Right after a tab click the previous tier's rows are still in state
  // but requestUrl has moved on: that's pending, not "the new tier is
  // empty". Pending shows nothing actionable -- no rows, no Call
  // Explorer link, no export, no truncation notice -- so stale rows
  // can't be clicked or exported under the new filters.
  const stale = !!response && response.url !== requestUrl
  const pending = loading || stale
  const calls = pending ? [] : response?.data?.items || []
  const truncated = !pending && !!response?.data?.truncated

  const filtered = useMemo(() => filterCallsByTier(calls, activeTier), [calls, activeTier])

  // AdvancedTable owns sorting now (clickable Date / Agent / Scorecard /
  // Score / Duration, asc & desc). Numeric `value`s so Score and
  // Duration sort numerically; Date sorts on the epoch ms. Every cell
  // carries the is-selected class -- AdvancedTable overrides a row-level
  // className, so the highlight rides on the cells instead of the <tr>.
  const columns = [
    { accessor: 'date', label: 'Date' },
    { accessor: 'agent', label: 'Agent' },
    { accessor: 'scorecard', label: 'Scorecard' },
    { accessor: 'score', label: 'Score' },
    { accessor: 'duration', label: 'Dur.' },
  ]

  const rows = useMemo(() => {
    return filtered.map((call) => {
      const scored = isCallScored(call)
      const pct = scored ? callPercentage(call) : null
      const tier = tierForCall(call)
      const duration = durationMinutes(call)
      const isSelected = selectedCall?.call_id === call.call_id && selectedCall?.sid === call.sid
      const agentName = agentNames[call.user_id] || `Agent ${call.user_id}`
      const scorecardName = scorecardNames[call.sid] || '—'
      const cellClass = (extra) => classNames(extra, { 'is-selected': isSelected })
      const timestamp = call.start_time ? new Date(call.start_time).getTime() : 0
      return {
        rowKey: `${call.call_id}-${call.sid}`,
        date: {
          as: (
            <button type="button" className="table-link" onClick={() => setSelectedCall(call)}>
              {formatTimestamp(call.start_time)}
            </button>
          ),
          value: timestamp,
          cellProps: { className: cellClass() },
        },
        agent: { as: agentName, value: agentName, cellProps: { className: cellClass() } },
        scorecard: {
          as: scorecardName,
          value: scorecardName,
          cellProps: { className: cellClass('scored-calls-scorecard') },
        },
        score: {
          as: scored ? (
            <>
              {isNil(pct) ? '—' : `${pct.toFixed(0)}%`}
              <TierBadge tier={tier} />
            </>
          ) : (
            <span className="scored-calls-na">N/A</span>
          ),
          // Sort N/A rows to the bottom of the Score column.
          value: scored && !isNil(pct) ? pct : null,
          cellProps: { className: cellClass('scored-calls-score') },
        },
        duration: {
          as: isNil(duration) ? '—' : `${duration}m`,
          value: isNil(duration) ? null : duration,
          cellProps: { className: cellClass() },
        },
      }
    })
  }, [filtered, selectedCall, agentNames, scorecardNames])

  const selectedAgentName = selectedCall ? agentNames[selectedCall.user_id] || null : null
  const selectedScorecardName = selectedCall ? scorecardNames[selectedCall.sid] || null : null

  return (
    <div className="scored-calls-browser">
      <div className="scored-calls-toolbar">
        {!isEmpty(filtered) && (
          <div className="scored-calls-tab-actions">
            <ExternalCallIdsLink
              className="scored-calls-explorer-link"
              callIds={filtered.map((call) => call.call_id)}
              label={`Open ${filtered.length} in Call Explorer`}
              channelType={scopeFilters?.callType}
              // The drawer's scope pins the scorecard (a criterion drilldown
              // narrows to that criterion's scorecard); the filter bar this
              // link reads by default doesn't know about that narrowing.
              scorecardSids={scopeFilters?.scorecards?.map((scorecard) => scorecard.value)}
            />
            <ExportCsvButton
              data-testid="scored-calls-export"
              onClick={() =>
                downloadCsvFile(
                  buildScoredCallsCsvName(scopeFilters),
                  [
                    'Call ID',
                    'Agent',
                    // Sits beside Agent as it does in the table (TREN-517):
                    // without it, grouping the export by scorecard means
                    // re-joining the column by hand.
                    'Scorecard',
                    'Start time',
                    'Duration (min)',
                    'Score %',
                    'Tier',
                    'Call type',
                    'Campaign',
                  ],
                  filtered.map((call) => {
                    const scored = isCallScored(call)
                    const pct = scored ? callPercentage(call) : null
                    return [
                      call.call_id,
                      agentNames[call.user_id] || call.user_id,
                      // Raw sid when the name can't be resolved, matching the
                      // Agent column -- an identifier is worth more than the
                      // table's em-dash in something filtered in a sheet.
                      scorecardNames[call.sid] || call.sid || '',
                      call.start_time || '',
                      durationMinutes(call) ?? '',
                      scored ? (isNil(pct) ? '' : pct) : 'N/A',
                      // No tier for N/A calls -- leave the Tier column blank
                      // rather than leaking the "N/A" score sentinel into it.
                      scored ? tierForCall(call) : '',
                      call.call_type || '',
                      call.voip_campaign_name || '',
                    ]
                  })
                )
              }
            />
          </div>
        )}
        <SegmentedControl
          small
          items={[
            { accessor: TIER.ALL, label: 'All' },
            { accessor: TIER.BELOW, label: 'Below threshold' },
            { accessor: TIER.ABOVE, label: 'Above threshold' },
          ].map((tab) => ({
            accessor: tab.accessor,
            active: activeTier === tab.accessor,
            onClick: () => setActiveTier(tab.accessor),
            label: tab.label,
            // Only a count learned from an un-truncated fetch is shown; a
            // count over the first `limit` rows would read as a wrong
            // total next to the Overview cards.
            badge: knownCounts[tab.accessor],
          }))}
        />
      </div>

      {truncated && (
        <p className="scored-calls-truncated">
          Showing the first {limit}. Narrow your filters to see more.
        </p>
      )}

      <div className="scored-calls-body">
        <div className="scored-calls-list">
          {pending && !response && (
            <div className="scored-calls-loader">
              <Loader centered />
            </div>
          )}
          {error && <div className="entity-drawer-error">Couldn&apos;t load calls.</div>}
          {!pending && !error && isEmpty(filtered) && (
            <div className="entity-drawer-empty">
              No calls match this filter in the current window.
            </div>
          )}
          {/* Across a tier refetch the table stays mounted and shows its
              own loader, so the user's sort survives even when the
              previous tier's rows filter down to nothing (Below -> Above).
              A scope change nulls the response instead, so it and the
              initial load start a fresh table behind the loader above. */}
          {!error && response && (pending || !isEmpty(filtered)) && (
            <AdvancedTable
              loading={pending}
              rows={rows}
              columns={columns}
              index="rowKey"
              defaultOrder="desc"
              defaultOrderBy="date"
              // Without this the table renders every fetched row into the DOM.
              pagination
              rowsPerPage={50}
              itemName="call"
              hoverable
              wrapColumnContent={false}
            />
          )}
        </div>
        {selectedCall && (
          <ScoredCallDetailPanel
            call={selectedCall}
            orgId={orgId}
            agentName={selectedAgentName}
            scorecardName={selectedScorecardName}
            onClose={() => setSelectedCall(null)}
          />
        )}
      </div>
    </div>
  )
}
